import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ReferenceCodeGenerator = ({ selections }) => {
  const [referenceCode, setReferenceCode] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    generateReferenceCode();
  }, [selections]);

  const generateReferenceCode = () => {
    const timestamp = Date.now()?.toString(36)?.toUpperCase();
    const random = Math.random()?.toString(36)?.substring(2, 8)?.toUpperCase();
    const code = `EPX-${timestamp}-${random}`;
    setReferenceCode(code);
  };

  const handleCopy = () => {
    navigator.clipboard?.writeText(referenceCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownload = () => {
    const content = `
EPOXY FLOORING REFERENCE CODE
==============================

Reference Code: ${referenceCode}
Generated: ${new Date()?.toLocaleString('en-US', { 
  year: 'numeric', 
  month: 'long', 
  day: 'numeric',
  hour: '2-digit',
  minute: '2-digit'
})}

SELECTION DETAILS
-----------------
Floor Type: ${selections?.floorType?.name || 'Not selected'}
Price: $${selections?.floorType?.price?.toFixed(2) || '0.00'} per ${selections?.floorType?.unit || 'sq ft'}

Base Color: ${selections?.baseColor?.name || 'Not selected'}
Flake Colors: ${selections?.flakeColors?.map(c => c?.name)?.join(', ') || 'None'}
Finish: ${selections?.finish || 'Not selected'}

Room: ${selections?.room?.name || 'Not selected'}
Intensity: ${selections?.intensity || 0}%
Design Heaviness: ${selections?.heaviness || 0}%

NEXT STEPS
----------
1. Save this reference code for your records
2. Contact us to schedule a consultation
3. Share this code with our team for accurate pricing

Thank you for using EpoxyViz Pro!
    `?.trim();

    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `epoxy-reference-${referenceCode}.txt`;
    link?.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6 space-y-6">
      <div className="text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-success/10 rounded-full mb-4">
          <Icon name="CheckCircle" size={32} color="var(--color-success)" />
        </div>
        <h2 className="text-2xl font-bold text-text-primary mb-2">
          Design Complete!
        </h2>
        <p className="text-text-secondary">
          Your epoxy flooring design has been saved with the reference code below
        </p>
      </div>
      <div className="bg-muted border-2 border-dashed border-border rounded-lg p-6">
        <label className="block text-sm font-medium text-text-secondary mb-2 text-center">
          Your Reference Code
        </label>
        <div className="text-center font-mono text-3xl font-bold text-primary mb-4 tracking-wider">
          {referenceCode}
        </div>
        <div className="flex gap-3 justify-center">
          <Button
            variant={copied ? 'success' : 'default'}
            onClick={handleCopy}
            iconName={copied ? 'Check' : 'Copy'}
            iconPosition="left"
            iconSize={18}
          >
            {copied ? 'Copied!' : 'Copy Code'}
          </Button>
          <Button
            variant="outline"
            onClick={handleDownload}
            iconName="Download"
            iconPosition="left"
            iconSize={18}
          >
            Download Details
          </Button>
        </div>
      </div>
      <div className="border-t border-border pt-6 space-y-4">
        <h3 className="text-lg font-semibold text-text-primary">Selection Summary</h3>
        
        <div className="grid gap-3">
          <div className="flex items-start gap-3 p-3 bg-surface rounded-lg">
            <Icon name="Grid3x3" size={20} className="text-primary mt-0.5" />
            <div className="flex-1">
              <div className="text-sm text-text-secondary">Floor Type</div>
              <div className="font-medium text-text-primary">{selections?.floorType?.name}</div>
              <div className="text-sm text-text-secondary">
                ${selections?.floorType?.price?.toFixed(2)} per {selections?.floorType?.unit}
              </div>
            </div>
          </div>

          <div className="flex items-start gap-3 p-3 bg-surface rounded-lg">
            <Icon name="Palette" size={20} className="text-primary mt-0.5" />
            <div className="flex-1">
              <div className="text-sm text-text-secondary">Colors</div>
              <div className="font-medium text-text-primary">{selections?.baseColor?.name}</div>
              {selections?.flakeColors?.length > 0 && (
                <div className="text-sm text-text-secondary">
                  + {selections?.flakeColors?.map(c => c?.name)?.join(', ')}
                </div>
              )}
            </div>
          </div>

          <div className="flex items-start gap-3 p-3 bg-surface rounded-lg">
            <Icon name="Home" size={20} className="text-primary mt-0.5" />
            <div className="flex-1">
              <div className="text-sm text-text-secondary">Room</div>
              <div className="font-medium text-text-primary">{selections?.room?.name}</div>
            </div>
          </div>

          <div className="flex items-start gap-3 p-3 bg-surface rounded-lg">
            <Icon name="Settings" size={20} className="text-primary mt-0.5" />
            <div className="flex-1">
              <div className="text-sm text-text-secondary">Configuration</div>
              <div className="font-medium text-text-primary capitalize">{selections?.finish} Finish</div>
              <div className="text-sm text-text-secondary">
                Intensity: {selections?.intensity}% • Heaviness: {selections?.heaviness}%
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
        <div className="flex gap-3">
          <Icon name="Info" size={20} className="text-primary mt-0.5 flex-shrink-0" />
          <div className="text-sm text-text-primary">
            <strong>Important:</strong> Save this reference code to retrieve your design later or share it with our team for accurate pricing and consultation.
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReferenceCodeGenerator;